import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import messages from '../../statics/messages.json';
import viewIcon from '../../statics/images/view.png';
import ReceiveContext from '../../context/ReceiveContext';

const DUE_DAYS_OPTIONS = [
    { label: 'Any', value: '' },
    { label: 'Due today / overdue', value: '1' },
    { label: 'Due within 7 days', value: '2' },
    { label: 'Due within 21 days', value: '3' },
    { label: 'Due within 28 days', value: '4' },
];

const PurchaseOrdersArchive = () => {
    const navigate = useNavigate();
    const { fetchArchivedPOs, updatePOArchivePoLineItem } = ReceiveContext();

    // Data
    const [data, setData] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    // Sorting
    const [sortCol, setSortCol] = useState('');
    const [sortDir, setSortDir] = useState('descending');

    // Draft text states — controlled inputs, no API trigger
    const [search, setSearch] = useState('');
    const [poSearch, setPoSearch] = useState('');
    const [vendorSearch, setVendorSearch] = useState('');
    const [partSearch, setPartSearch] = useState('');
    const [statusSearch, setStatusSearch] = useState('');

    // Applied text state — only updates on Search button / Enter
    const [applied, setApplied] = useState({
        search: '', poSearch: '', vendorSearch: '', partSearch: '', statusSearch: '',
    });

    // Non-text filters — apply immediately on change
    const [dueDays, setDueDays] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [showFilters, setShowFilters] = useState(false);

    // Unarchive
    const [unarchivingIds, setUnarchivingIds] = useState(new Set());
    const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3500);
        return () => clearTimeout(t);
    }, [toast]);

    const handleUnarchive = async (line) => {
        const lineId = line.id;
        const poNum = line.po?.ponum;
        if (!lineId) return;
        setUnarchivingIds(prev => new Set(prev).add(lineId));
        const result = await updatePOArchivePoLineItem(poNum, lineId, false);
        setUnarchivingIds(prev => { const s = new Set(prev); s.delete(lineId); return s; });
        if (result?.success) {
            setToast({ type: 'success', message: `PO line #${line.linenum ?? lineId} unarchived successfully.` });
            setData(prev => prev.filter(l => l.id !== lineId));
            setTotalElements(prev => Math.max(0, prev - 1));
        } else {
            setToast({ type: 'error', message: result?.message || `Failed to unarchive PO line #${line.linenum ?? lineId}.` });
        }
    };

    const hasActiveFilters =
        applied.search || applied.poSearch || applied.vendorSearch ||
        applied.partSearch || applied.statusSearch || dueDays || startDate || endDate;

    // API fetch — triggered by applied text filters, pagination, sort, dates, dueDays
    useEffect(() => {
        const run = async () => {
            setLoading(true);
            setError(null);
            const resp = await fetchArchivedPOs(
                (page - 1) * perPage, perPage,
                applied.search, sortCol, sortDir,
                applied.poSearch, applied.partSearch, applied.vendorSearch,
                applied.statusSearch, dueDays,
                startDate, endDate
            );
            if (resp) {
                setData(resp.content || []);
                setTotalElements(resp.totalElements || 0);
                setTotalPages(resp.totalPages || 0);
            } else {
                setError('Failed to load archived purchase orders. Please try again.');
            }
            setLoading(false);
        };
        run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, sortCol, sortDir, dueDays, startDate, endDate, applied]);

    // Commit draft text values → triggers the useEffect above
    const applySearch = () => {
        setPage(1);
        setApplied({ search, poSearch, vendorSearch, partSearch, statusSearch });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') applySearch();
    };

    const clearFilters = () => {
        setSearch(''); setPoSearch(''); setVendorSearch(''); setPartSearch(''); setStatusSearch('');
        setDueDays(''); setStartDate(''); setEndDate('');
        setSortCol(''); setSortDir('descending');
        setPage(1);
        setApplied({ search: '', poSearch: '', vendorSearch: '', partSearch: '', statusSearch: '' });
    };

    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir(d => d === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortCol(col);
            setSortDir('ascending');
        }
        setPage(1);
    };

    const getRowClass = (date) => {
        if (!date) return '';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(date);
        target.setHours(0, 0, 0, 0);
        const diff = Math.ceil((target - today) / 86400000);
        if (diff <= 0) return 'table-danger';
        if (diff <= 7) return 'table-warning';
        if (diff <= 21) return 'table-info';
        return '';
    };

    const getStatusBadge = (status) => {
        if (!status) return <span className="text-muted">—</span>;
        const s = status.toLowerCase();
        let cls = 'bg-secondary';
        if (s.includes('open') || s.includes('pending')) cls = 'bg-primary';
        else if (s.includes('complete') || s.includes('received') || s.includes('closed')) cls = 'bg-success';
        else if (s.includes('partial')) cls = 'bg-warning text-dark';
        else if (s.includes('cancel')) cls = 'bg-danger';
        return <span className={`badge ${cls}`} style={{ fontSize: '11px' }}>{status}</span>;
    };

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <i className="fa fa-sort text-muted ms-1" style={{ fontSize: '11px' }} />;
        return sortDir === 'ascending' ? <i className="fa fa-sort-up ms-1" /> : <i className="fa fa-sort-down ms-1" />;
    };

    const startIdx = (page - 1) * perPage;

    return (
        <div className="box-body b-white rounded-2">
            {/* ── Header ── */}
            <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                    <i className="fa fa-archive text-secondary" style={{ fontSize: '18px' }} />
                    <h5 className="m-0">Purchase Orders Archive</h5>
                    {!loading && totalElements > 0 && (
                        <span className="badge bg-secondary rounded-pill">{totalElements.toLocaleString()} records</span>
                    )}
                </div>
                <div className="d-flex gap-2">
                    {hasActiveFilters && (
                        <button className="btn btn-sm btn-outline-danger" onClick={clearFilters}>
                            <i className="fa fa-times me-1" />Clear Filters
                        </button>
                    )}
                    <button
                        className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setShowFilters(v => !v)}
                    >
                        <i className="fa fa-sliders me-1" />
                        Advanced Filters
                        {hasActiveFilters && <span className="ms-1 badge bg-danger rounded-pill" style={{ fontSize: '10px' }}>!</span>}
                    </button>
                </div>
            </div>

            {/* ── Advanced Filter Panel ── */}
            {showFilters && (
                <div className="border-bottom p-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="row g-2">
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">PO #</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Exact PO number"
                                value={poSearch}
                                onChange={e => setPoSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Vendor</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Exact vendor name"
                                value={vendorSearch}
                                onChange={e => setVendorSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Part #</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Part number"
                                value={partSearch}
                                onChange={e => setPartSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Status</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="PO line status"
                                value={statusSearch}
                                onChange={e => setStatusSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Due Window</label>
                            <select
                                className="form-select form-select-sm"
                                value={dueDays}
                                onChange={e => { setDueDays(e.target.value); setPage(1); }}
                            >
                                {DUE_DAYS_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Due Date From</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={startDate}
                                onChange={e => { setStartDate(e.target.value); setPage(1); }}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Due Date To</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={endDate}
                                onChange={e => { setEndDate(e.target.value); setPage(1); }}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end gap-1">
                            <button className="btn btn-sm btn-primary flex-grow-1" onClick={applySearch}>
                                <i className="fa fa-search me-1" />Search
                            </button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters} title="Reset all">
                                <i className="fa fa-refresh" />
                            </button>
                        </div>
                    </div>

                    <div className="mt-2 d-flex gap-3 flex-wrap" style={{ fontSize: '12px' }}>
                        <span className="d-flex align-items-center gap-1">
                            <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', display: 'inline-block' }} />
                            Overdue / due today
                        </span>
                        <span className="d-flex align-items-center gap-1">
                            <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#fff3cd', border: '1px solid #ffeeba', display: 'inline-block' }} />
                            Due within 7 days
                        </span>
                        <span className="d-flex align-items-center gap-1">
                            <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#d1ecf1', border: '1px solid #bee5eb', display: 'inline-block' }} />
                            Due within 21 days
                        </span>
                    </div>
                </div>
            )}

            {/* ── Toolbar ── */}
            <div className="d-flex align-items-center justify-content-between px-2 py-2">
                <div className="d-flex align-items-center gap-2">
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={perPage}
                        onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    <span className="text-muted small">per page</span>
                </div>
                <div className="d-flex gap-1" style={{ width: '380px' }}>
                    <div className="input-group input-group-sm flex-grow-1">
                        <span className="input-group-text bg-white">
                            <i className="fa fa-search text-muted" />
                        </span>
                        <input
                            type="text"
                            className="form-control border-start-0"
                            placeholder="Search status, part, vendor, PO #, due date..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {search && (
                            <button className="btn btn-outline-secondary" onClick={() => { setSearch(''); setApplied(a => ({ ...a, search: '' })); }}>
                                <i className="fa fa-times" />
                            </button>
                        )}
                    </div>
                    <button className="btn btn-sm btn-primary px-3" onClick={applySearch}>
                        Search
                    </button>
                </div>
            </div>

            {/* ── Error ── */}
            {error && (
                <div className="mx-2 mb-2 alert alert-danger py-2 d-flex align-items-center gap-2">
                    <i className="fa fa-exclamation-triangle" />
                    {error}
                    <button className="btn btn-sm btn-outline-danger ms-auto" onClick={applySearch}>Retry</button>
                </div>
            )}

            {/* ── Toast ── */}
            {toast && (
                <div className={`mx-2 mb-2 alert alert-${toast.type === 'success' ? 'success' : 'danger'} py-2 d-flex align-items-center gap-2`}>
                    <i className={`fa ${toast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} />
                    {toast.message}
                    <button type="button" className="btn-close ms-auto" style={{ fontSize: '12px' }} onClick={() => setToast(null)} />
                </div>
            )}

            {/* ── Table ── */}
            <div className="px-2">
                <div className="table-responsive bridge-manual-scroll" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    <table className="table table-bordered table-hover table-sm mb-0">
                        <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr>
                                <th className="text-center" style={{ minWidth: '46px', width: '46px' }}>#</th>
                                <th className="text-center" style={{ minWidth: '120px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('ponum')}>
                                    PO # <SortIcon col="ponum" />
                                </th>
                                <th className="text-center" style={{ minWidth: '70px' }}>Line</th>
                                <th className="text-center" style={{ minWidth: '190px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('vendor_name')}>
                                    Vendor <SortIcon col="vendor_name" />
                                </th>
                                <th className="text-center" style={{ minWidth: '140px' }}>Part #</th>
                                <th className="text-center" style={{ minWidth: '220px' }}>Part Description</th>
                                <th className="text-center" style={{ minWidth: '80px' }}>Qty</th>
                                <th className="text-center" style={{ minWidth: '80px' }}>Rcv'd</th>
                                <th className="text-center" style={{ minWidth: '130px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('duedate')}>
                                    Due Date <SortIcon col="duedate" />
                                </th>
                                <th className="text-center" style={{ minWidth: '110px' }}>Status</th>
                                <th className="text-center" style={{ minWidth: '110px', width: '110px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="11" className="text-center py-5">
                                        <div className="spinner-border text-secondary spinner-border-sm me-2" role="status" />
                                        <span className="text-muted">Loading archived purchase orders...</span>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="11" className="text-center text-muted py-5">
                                        <i className="fa fa-archive fa-2x mb-2 d-block" style={{ color: '#dee2e6' }} />
                                        {hasActiveFilters ? 'No archived PO lines match the current filters.' : 'No archived purchase orders found.'}
                                    </td>
                                </tr>
                            ) : (
                                data.map((line, i) => (
                                    <tr key={`arch-po-${line.id ?? i}`} className={getRowClass(line.duedate)}>
                                        <td className="text-center align-middle text-muted small">{startIdx + i + 1}</td>
                                        <td className="text-center align-middle">
                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                <a href={`https://gigaflightinc.cetecerp.com/purchaseorder/${line.po?.id}/view`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontWeight: 500 }} title="View PO in ERP">
                                                    {line.po?.ponum || 'N/A'}
                                                </a>
                                                <img src={viewIcon} style={{ width: '15px', opacity: 0.6 }} alt="" />
                                            </div>
                                        </td>
                                        <td className="text-center align-middle">{line.linenum ?? 'N/A'}</td>
                                        <td className="text-center align-middle">
                                            {line.po?.vennum ? (
                                                <a href={`https://gigaflightinc.cetecerp.com/vendor/${line.po.vennum}/view`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }} title="View vendor in ERP">
                                                    {line.po?.vendorName || 'N/A'}
                                                </a>
                                            ) : (line.po?.vendorName || 'N/A')}
                                        </td>
                                        <td className="text-center align-middle">
                                            <span title={line.prcpart || ''}>
                                                {line.prcpart ? line.prcpart.substring(3) : (line.part?.prcpart || 'N/A')}
                                            </span>
                                        </td>
                                        <td className="align-middle">
                                            <div title={line.part?.description || ''} style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {line.part?.description || '—'}
                                            </div>
                                        </td>
                                        <td className="text-center align-middle">{line.qty ?? line.quantity ?? '—'}</td>
                                        <td className="text-center align-middle">{line.receivedqty ?? line.received_qty ?? '—'}</td>
                                        <td className="text-center align-middle">{line.duedate || 'N/A'}</td>
                                        <td className="text-center align-middle">{getStatusBadge(line.status)}</td>
                                        <td className="text-center align-middle">
                                            {(() => {
                                                const busy = unarchivingIds.has(line.id);
                                                return (
                                                    <button
                                                        className="btn btn-sm btn-outline-warning"
                                                        style={{ fontSize: '11px', whiteSpace: 'nowrap' }}
                                                        disabled={busy}
                                                        onClick={() => handleUnarchive(line)}
                                                        title={`Unarchive PO line #${line.linenum}`}
                                                    >
                                                        {busy
                                                            ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Restoring...</>
                                                            : <><i className="fa fa-undo me-1" />Unarchive</>
                                                        }
                                                    </button>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Pagination ── */}
            {!loading && totalPages > 0 && (
                <div className="d-flex justify-content-between align-items-center px-2 py-2 border-top">
                    <span className="text-muted small">
                        Showing <strong>{startIdx + 1}</strong>–<strong>{Math.min(startIdx + perPage, totalElements)}</strong> of{' '}
                        <strong>{totalElements.toLocaleString()}</strong> archived PO lines
                    </span>
                    <ul className="pagination pagination-sm mb-0 align-items-center">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                        </li>
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Previous</button>
                        </li>
                        {totalPages > 1 && (
                            <li className="page-item">
                                <select className="form-select form-select-sm mx-2" style={{ width: 'auto' }} value={page} onChange={e => setPage(Number(e.target.value))}>
                                    {[...Array(totalPages)].map((_, i) => (
                                        <option key={i} value={i + 1}>Page {i + 1} of {totalPages}</option>
                                    ))}
                                </select>
                            </li>
                        )}
                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Next</button>
                        </li>
                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default PurchaseOrdersArchive;
